import {
  ArrowDownToLine,
  BookOpen,
  CheckCircle2,
  Download,
  FileText,
  Gift,
  Layers3,
  Megaphone,
  Palette,
  Search,
  Sparkles,
} from "lucide-react";
import { AppSidebarPortal } from "../components/AppShell";
import { DashboardWorkspaceNav } from "../components/DashboardWorkspaceNav";
import { usePortal } from "../state/PortalContext";

export const bonusDownloads = [
  {
    number: "01",
    title: "Beginner’s KDP Launch Blueprint",
    eyebrow: "21-page premium PDF",
    description:
      "A clear, visual path from book idea to upload-ready files, including production choices, preflight checks, and launch tracking.",
    details: ["Launch roadmap", "KDP setup", "Preflight checklists"],
    preview: "/paperkingdom/bonuses/previews/bonus-01-blueprint.png",
    file: "/paperkingdom/bonuses/files/Bonus-01-Beginners-KDP-Launch-Blueprint.pdf",
    icon: BookOpen,
  },
  {
    number: "02",
    title: "Canva Cover Design Starter Kit",
    eyebrow: "Canva access PDF • 16 editable pages",
    description:
      "Open the protected Canva template and create your own editable copy with cover concepts, palettes, type pairings, and safe-area guidance.",
    details: ["Canva template", "Cover concepts", "Type + color systems"],
    preview: "/paperkingdom/bonuses/previews/bonus-02-cover-kit.png",
    file: "/paperkingdom/bonuses/files/Bonus-02-Canva-Cover-Design-Starter-Kit-ACCESS.pdf",
    icon: Palette,
  },
  {
    number: "03",
    title: "KDP Niche Research Workbook",
    eyebrow: "29-page printable workbook",
    description:
      "A structured research book for capturing reader evidence, comparing listings, scoring opportunities, and reaching a visible go-or-hold decision.",
    details: ["Audience evidence", "Competitor review", "Opportunity scoring"],
    preview: "/paperkingdom/bonuses/previews/bonus-03-workbook.png",
    file: "/paperkingdom/bonuses/files/Bonus-03-KDP-Niche-Research-Workbook.pdf",
    icon: Search,
  },
  {
    number: "04",
    title: "Book Description Swipe File",
    eyebrow: "16-page premium PDF",
    description:
      "Truthful, adaptable frameworks for shaping titles, subtitles, hooks, descriptions, feature bullets, calls to action, and author bios.",
    details: ["Listing formulas", "Hook frameworks", "Final quality gate"],
    preview: "/paperkingdom/bonuses/previews/bonus-04-swipe-file.png",
    file: "/paperkingdom/bonuses/files/Bonus-04-Book-Description-Swipe-File.pdf",
    icon: FileText,
  },
  {
    number: "05",
    title: "Product Mockup & Promotion Pack",
    eyebrow: "Canva access PDF • 26 editable layouts",
    description:
      "Two protected Canva template sets with square and vertical layouts for cover reveals, product mockups, benefit cards, announcements, and launch content.",
    details: ["Square templates", "Vertical templates", "Launch graphics"],
    preview: "/paperkingdom/bonuses/previews/bonus-05-promotion-pack.png",
    file: "/paperkingdom/bonuses/files/Bonus-05-Product-Mockup-and-Promotion-Pack-ACCESS.pdf",
    icon: Megaphone,
  },
] as const;

export function BonusesPage() {
  const { entitlements, projects, storageMode } = usePortal();

  return (
    <>
      <AppSidebarPortal>
        <DashboardWorkspaceNav
          active="bonuses"
          projectCount={projects.length}
          cloudWorkspace={storageMode === "cloud"}
          hasResellerRights={entitlements.includes("paper-kingdom-reseller")}
        />
      </AppSidebarPortal>

      <main className="bonuses-page">
        <section className="bonuses-hero" aria-labelledby="bonuses-title">
          <div className="bonuses-hero__copy">
            <span className="bonuses-kicker">
              <Sparkles size={14} aria-hidden="true" /> Your complete bonus vault
            </span>
            <h1 id="bonuses-title">Everything extra, ready to use.</h1>
            <p>
              Five finished resources for researching, creating, packaging, and promoting your next book. Download one now or take the complete vault.
            </p>
            <div className="bonuses-hero__actions">
              <a
                className="bonuses-primary-action"
                href="/paperkingdom/bonuses/files/Paper-Kingdom-Complete-Bonus-Vault.zip"
                download
              >
                <ArrowDownToLine size={18} /> Download all bonuses
              </a>
              <span>One ZIP • all five finished PDFs</span>
            </div>
          </div>
          <div className="bonuses-hero__summary" aria-label="Bonus vault summary">
            <span className="bonuses-hero__gift" aria-hidden="true">
              <Gift size={36} />
            </span>
            <div>
              <strong>5</strong>
              <small>premium bonuses</small>
            </div>
            <div>
              <strong>3</strong>
              <small>Canva templates</small>
            </div>
            <div>
              <strong>100%</strong>
              <small>ready to download</small>
            </div>
          </div>
        </section>

        <section className="bonuses-library" aria-labelledby="bonus-library-title">
          <header className="bonuses-library__header">
            <div>
              <span>Included with Paper Kingdom</span>
              <h2 id="bonus-library-title">Choose your bonus</h2>
            </div>
            <p>
              Canva bonuses download as beautifully designed access PDFs. Open the button inside each PDF to create an editable copy in your Canva account.
            </p>
          </header>

          <div className="bonus-download-grid">
            {bonusDownloads.map((bonus) => {
              const Icon = bonus.icon;
              return (
                <article className="bonus-download-card" key={bonus.number}>
                  <div className="bonus-download-card__preview">
                    <span>Bonus {bonus.number}</span>
                    <img src={bonus.preview} alt={`${bonus.title} cover preview`} />
                  </div>
                  <div className="bonus-download-card__content">
                    <div className="bonus-download-card__heading">
                      <span className="bonus-download-card__icon" aria-hidden="true">
                        <Icon size={18} />
                      </span>
                      <div>
                        <small>{bonus.eyebrow}</small>
                        <h3>{bonus.title}</h3>
                      </div>
                    </div>
                    <p>{bonus.description}</p>
                    <ul aria-label={`${bonus.title} includes`}>
                      {bonus.details.map((detail) => (
                        <li key={detail}>
                          <CheckCircle2 size={14} aria-hidden="true" /> {detail}
                        </li>
                      ))}
                    </ul>
                    <a className="bonus-download-card__button" href={bonus.file} download>
                      <Download size={16} /> Download PDF
                    </a>
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        <section className="bonuses-note" aria-label="Using your bonuses">
          <span aria-hidden="true">
            <Layers3 size={22} />
          </span>
          <div>
            <strong>Your Canva originals stay protected.</strong>
            <p>
              The template buttons create editable copies in the customer’s Canva account. The Paper Kingdom master designs are never changed.
            </p>
          </div>
        </section>
      </main>
    </>
  );
}
